import React from 'react';
import { Box, Button, LinearProgress, Typography } from '../../ui';
import type { VideoSeekGuardState } from '../../../hooks/useVideoSeekGuard';

interface VideoWatchGuardProps extends VideoSeekGuardState {
  // When false the chrome is hidden and children render bare (admins).
  enabled: boolean;
  // The player element (mux-player / wistia-player).
  children: React.ReactNode;
}

// "M:SS" — used for the watched figure, the warning, and the button label.
function formatTime(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Chrome around the learner video player that enforces watch-through:
 * a header line, the player, a status footer, and a watched-progress bar.
 *
 * Normal playback shows a neutral "Keep watching to continue." footer.
 * When the seek guard flags a skip-ahead, the footer turns amber with a
 * "Jump back to M:SS" action that returns the learner to the furthest
 * point they'd legitimately watched. Driven entirely by useVideoSeekGuard
 * — this component is presentational.
 */
export default function VideoWatchGuard({
  enabled,
  skipped,
  watchedSeconds,
  duration,
  jumpBack,
  children,
}: VideoWatchGuardProps) {
  if (!enabled) return <>{children}</>;

  const watchedLabel = formatTime(watchedSeconds);
  const progress = duration > 0 ? Math.min(100, (watchedSeconds / duration) * 100) : 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header — states the rule up front, matching the players the
          learners are used to elsewhere. */}
      <Box sx={{ pb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          Please watch the full video to continue
        </Typography>
      </Box>

      {children}

      {/* Status footer — neutral while watching, amber on skip-ahead. */}
      <Box
        sx={{
          mt: 1.5,
          px: 2,
          py: 1.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: skipped ? '#ffe082' : 'divider',
          bgcolor: skipped ? 'warning.softBg' : 'grey.50',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          transition: 'background-color 150ms ease, border-color 150ms ease',
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: skipped ? '#9a6700' : 'text.secondary' }}
        >
          {skipped
            ? `You skipped ahead. Please jump back to ${watchedLabel} to continue watching.`
            : 'Keep watching to continue.'}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" fontWeight={700} color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            Watched: {watchedLabel} of {formatTime(duration)}
          </Typography>
          {skipped && (
            <Button
              variant="contained"
              color="warning"
              onClick={jumpBack}
              sx={{ fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Jump back to {watchedLabel}
            </Button>
          )}
        </Box>
      </Box>

      {/* Watched-progress meter — brand gradient, matches the curriculum
          sidebar's progress bar. */}
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          mt: 1,
          height: 6,
          borderRadius: 999,
          bgcolor: 'grey.200',
          '& .MuiLinearProgress-bar': {
            borderRadius: 999,
            background: 'var(--brand-gradient)',
          },
        }}
      />
    </Box>
  );
}
